/**
 * EditorialFigureCaption — a numbered caption using
 * theme.figureNumberingPrefix, italic in the muted tone.
 *
 * Editorial reference: `.cap` in docs/design/journal-section-layouts.html
 *   serif, italic, var(--muted) ink; the leading "Figure N." / "Table N."
 *   label is non-italic in var(--ink).
 *
 * Theme-aware: the label ink reads roleColor(theme,'ink'); the caption
 * body reads roleColor(theme,'muted'). The number prefix honours
 * theme.figureNumberingPrefix ("Figure ", "Fig. ", "Table " override).
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { useFigureNumber, type FigureCounterChannel } from '../figureCounter';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

export interface EditorialFigureCaptionProps {
  theme: ReportTheme;
  /**
   * Figure/table number (1-based). When `figureKey` is supplied this is only
   * the FALLBACK used outside a FigureCounterProvider (standalone mounts,
   * unit tests); inside a report root the counter assigns the report-global
   * sequential number. Defaults to 1.
   */
  number?: number;
  children: React.ReactNode;
  /** Override the prefix (defaults to theme.figureNumberingPrefix → "Figure "). */
  prefix?: string;
  /**
   * Redhead review wave 3 (defect 5) — stable key for REPORT-GLOBAL sequential
   * numbering via the FigureCounter context (e.g. `climateProfile:monthly`).
   * Captions whose prefix reads as a TABLE ("Table …") number on the separate
   * 'table' channel so figures and tables each run 1, 2, 3… independently.
   * Omit (legacy callers) to keep the explicit `number` byte-identically.
   */
  figureKey?: string;
  /** Render as a <figcaption> instead of a <div> (default div). */
  as?: 'div' | 'figcaption';
  className?: string;
  style?: React.CSSProperties;
}

export function EditorialFigureCaption({
  theme,
  number = 1,
  children,
  prefix,
  figureKey,
  as = 'div',
  className,
  style,
}: EditorialFigureCaptionProps): React.ReactElement {
  // theme.figureNumberingPrefix is "Figure " by default; allow an explicit
  // override (e.g. "Table ") for table captions. Trim trailing space so we
  // control spacing, then re-add a single space before the number.
  const rawPrefix = prefix ?? theme.figureNumberingPrefix ?? 'Figure ';
  // Table-prefixed captions count on their own channel (Figure 1..N and
  // Table 1..N are independent sequences).
  const channel: FigureCounterChannel = /table/i.test(rawPrefix) ? 'table' : 'figure';
  // Hook is called unconditionally (null key ⇒ inert, returns `number`), so
  // legacy explicit-number callers never consume a sequence slot.
  const counterNumber = useFigureNumber(figureKey ?? null, number, channel);
  const resolvedNumber = figureKey != null ? counterNumber : number;
  const label = `${rawPrefix.trim()} ${resolvedNumber}.`;
  const Tag = as;

  return (
    <Tag
      className={className}
      // E2 (export rewrite) — a caption keeps with the exhibit it names on the
      // NATIVE print path: the native stylesheet binds this hook with
      // `break-before: avoid`, so a below-table caption can no longer open the
      // next page alone. Deliberately NOT `data-figure-caption`, which index.css
      // also binds on the Paged.js deck (the deck stays byte-identical until E3).
      data-native-figure-caption=""
      style={{
        fontStyle: 'italic',
        // 12px caption token (text-xs) — unified across editorial captions.
        fontSize: REPORT_TEXT_PX.label,
        lineHeight: 1.5,
        color: roleColor(theme, 'muted'),
        marginTop: 9,
        // William 2026-07-03 — breathing room between the caption and the
        // paragraph that follows (captions sat flush against body prose).
        marginBottom: 14,
        breakInside: 'avoid',
        ...style,
      }}
    >
      {/* Redhead wave 3 (defect 5b) — the label/caption separator is a
           NO-BREAK SPACE INSIDE the label span (not a whitespace-only text
           node between elements, which the print/PDF pipeline could collapse
           into the "Figure 1.Monthly…" run-on). Renders identically on
           screen; the caption can never lose the space after "Figure N.". */}
      <span style={{ fontStyle: 'normal', fontWeight: 600, color: roleColor(theme, 'ink') }}>
        {label}
        {'\u00A0'}
      </span>
      {children}
    </Tag>
  );
}
